import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  FileSearch,
  AlertTriangle,
  GitPullRequest,
  Scale,
  ExternalLink,
  Mail,
} from "lucide-react";
import SEO from "../components/SEO";

const principles = [
  {
    icon: FileSearch,
    title: "Convicted, charged, or indicted only",
    body: "We do not publish unsubstantiated allegations, anonymous tips, social-media accusations, or pre-arrest reports. Every record represents a person who has been formally charged, indicted, convicted, or pleaded guilty in a court of law, or who appears on an official government animal-abuse registry.",
  },
  {
    icon: ShieldCheck,
    title: "Authoritative sources, every time",
    body: "Every record links to at least one verifying source — a state or county animal-abuse registry (TN TBI, Henrico County VA, Suffolk County NY), a federal indictment or DOJ press release, an appellate decision, a state Attorney General release, or a published news account from a credentialed outlet. Records sourced from primary government documents carry a green VERIFIED badge.",
  },
  {
    icon: AlertTriangle,
    title: "Pending cases are clearly labeled",
    body: 'Cases that are charged or indicted but not yet adjudicated are explicitly flagged with an amber "ON TRIAL — PENDING" badge and the year is labeled "Indicted" rather than "Convicted." Users are warned not to treat these as proven crimes.',
  },
  {
    icon: GitPullRequest,
    title: "Corrections are a first-class citizen",
    body: "Every detail page has a Request Correction / Takedown button. Genuine claims (overturned, vacated, expunged, sealed, or mis-identified subject) are reviewed within 5 business days and corrected or removed with a public note. We err on the side of removing rather than disputing close calls.",
  },
  {
    icon: Scale,
    title: "We are not a background-check service",
    body: "Paws on Record is a public-interest research tool. We are not a Consumer Reporting Agency under the Fair Credit Reporting Act and the Service may not be used for employment, housing, lending, insurance, or other FCRA-covered decisions. We make this disclaimer prominent on every page.",
  },
];

export default function EditorialStandards() {
  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-14" data-testid="editorial-page">
      <SEO
        title="Editorial Standards · How We Verify Records"
        description="Paws on Record only publishes convicted, charged, or registered animal abusers from authoritative sources — TBI, county registries, DOJ indictments, appellate decisions, and credentialed news outlets."
        path="/editorial"
      />
      <div className="label-eyebrow text-red-600">Methodology · Editorial Standards</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
        How Paws on Record decides what to publish.
      </h1>
      <p className="text-lg text-zinc-700 mt-5 leading-relaxed max-w-2xl">
        We aggregate animal-cruelty conviction records to help shelters, rescues,
        adopters, and journalists make safer decisions. Our editorial standards
        keep this useful and trustworthy — and accountable when we get it wrong.
      </p>

      <div className="mt-10 border-2 border-zinc-900 p-6 bg-zinc-50">
        <div className="label-eyebrow mb-2">In one sentence</div>
        <p className="font-display text-2xl font-black tracking-tight leading-tight">
          We publish only court-record-backed cruelty cases, source every entry,
          flag pending trials, and remove anything we got wrong.
        </p>
      </div>

      <div className="mt-12 space-y-10">
        {principles.map((p, i) => (
          <section
            key={p.title}
            className="border-t border-zinc-300 pt-8"
            data-testid={`editorial-principle-${i}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-zinc-900 text-white border border-zinc-900 flex-shrink-0 flex items-center justify-center">
                <p.icon size={18} />
              </div>
              <div>
                <div className="label-eyebrow">§ {i + 1}</div>
                <h2 className="font-display font-black text-2xl tracking-tighter mt-1">
                  {p.title}
                </h2>
                <p className="text-zinc-700 leading-relaxed mt-3 text-[15px]">
                  {p.body}
                </p>
              </div>
            </div>
          </section>
        ))}
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Link
          to="/legal"
          className="border border-zinc-900 p-5 hover:bg-zinc-900 hover:text-white transition-colors group"
          data-testid="editorial-link-legal"
        >
          <div className="label-eyebrow group-hover:text-red-400">
            Read the full terms
          </div>
          <div className="font-display font-black text-xl mt-1 flex items-center gap-2">
            Terms, Privacy & Takedowns <ExternalLink size={16} />
          </div>
        </Link>
        <a
          href="mailto:info@pawsonrecord.com?subject=Paws%20on%20Record%20editorial%20question"
          className="border border-zinc-900 p-5 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors group"
          data-testid="editorial-link-email"
        >
          <div className="label-eyebrow group-hover:text-white">
            Press / journalism inquiries
          </div>
          <div className="font-display font-black text-xl mt-1 flex items-center gap-2">
            info@pawsonrecord.com <Mail size={16} />
          </div>
        </a>
      </div>

      <section
        className="mt-16 border-t border-zinc-300 pt-10"
        data-testid="editorial-press-statement"
        id="press"
      >
        <div className="label-eyebrow text-red-600">For Press · Quotable Statement</div>
        <h2 className="font-display font-black text-3xl tracking-tighter mt-2">
          Official statement
        </h2>
        <p className="text-zinc-700 mt-3 max-w-2xl">
          Journalists, shelter directors, and partners are welcome to quote the
          following statement directly. Attribute to <strong>Nova Naval, Founder</strong>.
        </p>

        <figure className="mt-8 border-l-4 border-red-600 bg-zinc-50 p-7 relative">
          <blockquote className="text-[17px] sm:text-lg leading-relaxed text-zinc-900 font-serif italic">
            &ldquo;Paws on Record is a public-interest transparency project
            operated by Refuel LLC. We aggregate convicted animal-abuser records
            from official state, federal, and court sources to help shelters and
            adopters make safer placement decisions. Our editorial standards
            are public at{" "}
            <span className="not-italic font-mono text-red-600">
              pawsonrecord.com/editorial
            </span>
            . I&rsquo;d be happy to discuss the registry, our methodology, and
            the broader animal-cruelty data gap.&rdquo;
          </blockquote>
          <figcaption className="mt-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm">
              <div className="font-display font-black text-base">— Nova Naval</div>
              <div className="text-zinc-600">Founder · Paws on Record</div>
            </div>
            <button
              type="button"
              onClick={() => {
                const txt = `"Paws on Record is a public-interest transparency project operated by Refuel LLC. We aggregate convicted animal-abuser records from official state, federal, and court sources to help shelters and adopters make safer placement decisions. Our editorial standards are public at pawsonrecord.com/editorial. I'd be happy to discuss the registry, our methodology, and the broader animal-cruelty data gap." — Nova Naval, Founder`;
                navigator.clipboard.writeText(txt);
                const btn = document.querySelector('[data-testid="editorial-quote-copy"]');
                if (btn) {
                  const orig = btn.innerText;
                  btn.innerText = "Copied ✓";
                  setTimeout(() => { btn.innerText = orig; }, 1800);
                }
              }}
              className="btn-outline text-xs"
              data-testid="editorial-quote-copy"
            >
              Copy quote
            </button>
          </figcaption>
        </figure>
      </section>

      <div className="mt-14 border border-zinc-300 p-5 bg-zinc-50 text-sm text-zinc-600 leading-relaxed">
        <strong className="text-zinc-900 uppercase text-[11px] tracking-widest font-mono block mb-2">
          Operating entity
        </strong>
        Paws on Record is operated by <strong>Refuel LLC</strong> as a
        public-interest transparency project. We do not accept payment, gifts,
        or pressure to add or remove individual records. Sponsor donations on
        the Donors Wall are clearly labeled and never affect editorial
        decisions.
      </div>
    </div>
  );
}
